import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, BookOpen, AlertCircle, ToggleLeft, ToggleRight,
  Square, CheckSquare, FormInput, MessageCircle, Send, Loader2
} from 'lucide-react';
import { contactFormFieldsAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ContactFormFieldsManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formFields, setFormFields] = useState([]);

  // Bulk actions state
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch form fields
  const fetchFormFields = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await contactFormFieldsAPI.getAdminFields();
      
      if (response?.data?.success) {
        console.log('📝 Fetched form fields:', response.data.data);
        setFormFields(response.data.data);
      } else {
        setError('Failed to fetch form fields');
        notify.error('Failed to fetch form fields', 'Loading Error');
      }
    } catch (err) {
      console.error('Error fetching form fields:', err);
      setError(`Error loading form fields: ${err.message}`);
      notify.error(`Error loading form fields: ${err.message}`, 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormFields();
  }, []);

  const handleInputChange = (index, field, value) => {
    const updatedFields = [...formFields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: value
    };
    setFormFields(updatedFields);
  };

  // Bulk actions functions
  const handleSelectField = (fieldId) => {
    setSelectedFields(prev => {
      const newSelected = prev.includes(fieldId)
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId];
      
      setShowBulkActions(newSelected.length > 0);
      setSelectAll(newSelected.length === formFields.length);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFields([]);
      setShowBulkActions(false);
      setSelectAll(false);
    } else {
      const allFieldIds = formFields.map(field => field.id);
      setSelectedFields(allFieldIds);
      setShowBulkActions(true);
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedFields.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await contactFormFieldsAPI.bulkUpdateStatus(selectedFields, is_active);
      
      if (response?.data?.success) {
        setSuccess(`${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''} ${is_active ? 'activated' : 'deactivated'} successfully`);
        notify.success(`${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''} ${is_active ? 'activated' : 'deactivated'} successfully`, 'Status Updated');
        setSelectedFields([]);
        setShowBulkActions(false);
        setSelectAll(false);
        fetchFormFields();
      } else {
        setError(response?.data?.message || 'Failed to update fields');
        notify.error(response?.data?.message || 'Failed to update fields', 'Update Error');
      }
    } catch (error) {
      console.error('Error updating fields:', error);
      setError('Failed to update fields');
      notify.error('Failed to update fields', 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFields.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await contactFormFieldsAPI.bulkDelete(selectedFields);
      
      if (response?.data?.success) {
        setSuccess(`${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''} deleted successfully`);
        notify.success(`${selectedFields.length} field${selectedFields.length !== 1 ? 's' : ''} deleted successfully`, 'Fields Deleted');
        setSelectedFields([]);
        setShowBulkActions(false);
        setSelectAll(false);
        fetchFormFields();
      } else {
        setError(response?.data?.message || 'Failed to delete fields');
        notify.error(response?.data?.message || 'Failed to delete fields', 'Delete Error');
      }
    } catch (error) {
      console.error('Error deleting fields:', error);
      setError('Failed to delete fields');
      notify.error('Failed to delete fields', 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const saveFormFields = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      console.log('📝 Sending form fields data:', formFields);
      console.log('📝 Form fields type:', typeof formFields);
      console.log('📝 Form fields is array:', Array.isArray(formFields));
      
      // Log each field to check for missing labels
      formFields.forEach((field, index) => {
        console.log(`📝 Field ${index + 1}:`, {
          id: field.id,
          field_name: field.field_name,
          field_label: field.field_label,
          field_placeholder: field.field_placeholder,
          is_required: field.is_required,
          is_active: field.is_active,
          sort_order: field.sort_order
        });
      });

      // Validate that all fields have labels
      const fieldsWithEmptyLabels = formFields.filter(field => !field.field_label || field.field_label.trim() === '');
      if (fieldsWithEmptyLabels.length > 0) {
        const fieldNames = fieldsWithEmptyLabels.map(field => field.field_name).join(', ');
        setError(`Please provide labels for the following fields: ${fieldNames}`);
        notify.error(`Please provide labels for the following fields: ${fieldNames}`, 'Validation Error');
        return;
      }

      const response = await contactFormFieldsAPI.updateFieldsBulk(formFields);
      
      if (response?.data?.success) {
        setSuccess('Form fields updated successfully!');
        notify.success('Form fields updated successfully!', 'Success');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update form fields');
        notify.error('Failed to update form fields', 'Update Error');
      }
    } catch (err) {
      console.error('Error updating form fields:', err);
      console.error('Error response:', err.response?.data);
      setError(`Error updating form fields: ${err.message}`);
      notify.error(`Error updating form fields: ${err.message}`, 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const clearMessage = () => {
    setError(null);
    setSuccess(null);
  };

  const getFieldTypeIcon = (fieldType) => {
    const icons = {
      'text': <FormInput className="w-4 h-4" />,
      'email': <Mail className="w-4 h-4" />,
      'tel': <Phone className="w-4 h-4" />,
      'select': <Settings className="w-4 h-4" />,
      'textarea': <MessageCircle className="w-4 h-4" />,
      'button': <Send className="w-4 h-4" />
    };
    return icons[fieldType] || <FormInput className="w-4 h-4" />;
  };

  const getFieldTypeLabel = (fieldType) => {
    const labels = {
      'text': 'Text Input',
      'email': 'Email Input',
      'tel': 'Phone Input',
      'select': 'Dropdown',
      'textarea': 'Text Area',
      'button': 'Button'
    };
    return labels[fieldType] || 'Text Input';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Contact Form Fields Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Manage labels and placeholders for contact form fields
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchFormFields}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={saveFormFields}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {(error || success) && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            error 
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
              : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          }`}>
            <div className="flex items-center">
              {error ? (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
              )}
              <p className={error ? 'text-red-800 dark:text-red-200' : 'text-green-800 dark:text-green-200'}>
                {error || success}
              </p>
            </div>
            <button
              onClick={clearMessage}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        {showBulkActions && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected
                </span>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleBulkStatusUpdate(true)}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                    Activate
                  </button>
                  
                  <button
                    onClick={() => handleBulkStatusUpdate(false)}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                    Deactivate
                  </button>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                  >
                    {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Form Field Configuration
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Edit the labels and placeholders for each form field. Changes will be reflected immediately on the contact form.
                </p>
              </div>
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                {selectAll ? (
                  <CheckSquare className="h-4 w-4 text-blue-600" />
                ) : (
                  <Square className="h-4 w-4 text-gray-400" />
                )}
                Select All
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {formFields.map((field, index) => (
              <div key={field.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleSelectField(field.id)}
                      className="flex items-center"
                    >
                      {selectedFields.includes(field.id) ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    {getFieldTypeIcon(field.field_type)}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {field.field_name.charAt(0).toUpperCase() + field.field_name.slice(1).replace(/_/g, ' ')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {getFieldTypeLabel(field.field_type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.is_active}
                        onChange={(e) => handleInputChange(index, 'is_active', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        onChange={(e) => handleInputChange(index, 'is_required', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Required</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Field Label
                    </label>
                    <input
                      type="text"
                      value={field.field_label}
                      onChange={(e) => handleInputChange(index, 'field_label', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter field label"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Field Placeholder
                    </label>
                    <input
                      type="text"
                      value={field.field_placeholder || ''}
                      onChange={(e) => handleInputChange(index, 'field_placeholder', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter field placeholder"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={field.sort_order}
                    onChange={(e) => handleInputChange(index, 'sort_order', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter sort order"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormFieldsManagement; 